import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { ID } from '../../_kernel/ID'
import { Board } from './Board'
import { Rate } from './Rate'
import { Room } from './Room'
import { Price } from '../../_kernel/Price'

export const AvailabilityValidation = z.object({
  id: z.instanceof(ID, { message: '[Availability] id is required' }),
  board: z.instanceof(Board, { message: '[Availability] board is required' }),
  rate: z.instanceof(Rate, { message: '[Availability] rate is required' }),
  room: z.instanceof(Room, { message: '[Availability] room is required' }),
  price: z.instanceof(Price, { message: '[Availability] price is required' }),
})

export class Availability extends Model {
  static create({ id, board, rate, room, price }: z.infer<typeof AvailabilityValidation>) {
    AvailabilityValidation.parse({ id, board, rate, room, price })
    return new Availability(id, board, rate, room, price)
  }

  static fromJSON(json: ReturnType<Availability['toJSON']>) {
    return new Availability(
      ID.create({ value: json.id }),
      Board.fromJSON(json.board),
      Rate.fromJSON(json.rate),
      Room.fromJSON(json.room),
      Price.fromJSON(json.price)
    )
  }

  constructor(
    private readonly _id: ID,
    private readonly _board: Board,
    private readonly _rate: Rate,
    private readonly _room: Room,
    private readonly _price: Price
  ) {
    super()
  }

  roomID() {
    return this._room.roomID()
  }

  board() {
    return this._board
  }

  boardName() {
    return this._board.name() ?? ''
  }

  boardCode() {
    return this._board.code() ?? ''
  }

  rate() {
    return this._rate
  }

  paymentPolicy() {
    return this._rate.paymentPolicy()
  }

  cancellationPolicyFromDate() {
    return this._rate.cancellationPolicyFromDate() ?? ''
  }

  cancellationPolicyAmount() {
    return this._rate.cancellationPolicyAmount() ?? 0
  }

  price() {
    return this._price
  }

  currency() {
    return this._price.currency()
  }

  priceDiff(priceBefore: Price) {
    return this._price.diff(priceBefore)
  }

  toJSON() {
    return {
      id: this._id.value,
      board: this._board.toJSON(),
      rate: this._rate.toJSON(),
      room: this._room.toJSON(),
      price: this._price.toJSON(),
    }
  }
}
